<script setup lang="ts">
import { ProposedDelivery, Subscription } from '@nuts/auto-delivery-sdk';
import { ref } from 'vue';

import AddedToSubscriptionActions from '@/components/auto-delivery/added-to-auto-delivery/AddedSubscriptionControls.vue';
import AddToCartOrCustomize from '@/components/base/add-to-cart/AddToCartOrCustomize.vue';
import SmallBodyText from '@/components/base/typography/SmallBodyText.vue';
import { useCallback } from '@/composables/useCallback';
import { getProposedDeliveryInterval } from '@/lib/autoDelivery';
import { useAutoDelivery } from '@/stores/autoDelivery';
import { sendProductSubscriptionCreatedEvent } from '@/utils/analytics/productSubscriptionCreatedEvent';
import { getDeliverySubscriptionBySku } from '@/utils/autoDelivery';
import { ProductCardData } from '@/utils/productCard';

const props = defineProps<{
  controlled?: boolean;
  delivery: ProposedDelivery;
  isDisabled?: boolean;
  isLoading?: boolean;
  mode: 'one-time' | 'recurring';
  product: ProductCardData;
  showManageButton?: boolean;
}>();

const emit = defineEmits<{
  added: [product: ProductCardData];
  removed: [product: ProductCardData];
}>();

const addedSubscription = ref<Subscription>();

const autoDeliveryStore = useAutoDelivery();

async function refreshOrders() {
  await autoDeliveryStore.getOrders({
    preloadPaymentMethod: false,
    preloadShippingAddresses: false,
  });
}

const onAddToAutoDelivery = useCallback(async () => {
  const interval =
    props.mode === 'recurring' ? getProposedDeliveryInterval(props.delivery, 'days') : undefined;

  await autoDeliveryStore.createSubscription({
    active: true,
    interval,
    nextShipOn: props.delivery.shipOn,
    offerLocation: 'Added to Auto-Delivery Modal',
    offerType: props.mode === 'recurring' ? 'IU Recurring' : 'IU Upsell',
    quantity: 1,
    shippingAddressId: props.delivery.shippingAddressId,
    sku: props.product.sku,
  });
  await refreshOrders();

  const updatedDelivery = autoDeliveryStore.orders.find(({ id }) => id === props.delivery.id);

  if (updatedDelivery) {
    addedSubscription.value = getDeliverySubscriptionBySku(updatedDelivery, props.product.sku);

    if (addedSubscription.value) {
      sendProductSubscriptionCreatedEvent({
        pageSection: 'Added to Auto-Delivery Modal',
        productKey: props.product.productKey,
        subscription: addedSubscription.value,
      });
    }
  }
});

const handleRemove = useCallback(async () => {
  if (!addedSubscription.value) return;
  await autoDeliveryStore.cancelSubscription(
    addedSubscription.value,
    'Removed with remove button',
    undefined,
    false,
  );
  await refreshOrders();
  addedSubscription.value = undefined;
});

async function onAddToAutoDeliveryClick() {
  if (!props.controlled) {
    await onAddToAutoDelivery.execute();
  }
  emit('added', props.product);
}

async function onRemoveClick() {
  if (!props.controlled) {
    await handleRemove.execute();
  }
  emit('removed', props.product);
}
</script>

<template>
  <Transition class="w-full" mode="out-in" name="fade">
    <AddedToSubscriptionActions
      v-if="addedSubscription"
      :delivery
      :isLoading="isLoading || handleRemove.isPending"
      :showManageButton
      :subscription="addedSubscription"
      @remove="onRemoveClick"
    />
    <AddToCartOrCustomize
      v-else-if="product.autoDeliveryEligible"
      class="gap-1 px-4 h-10 whitespace-nowrap"
      data-test="subscribe-cta-button"
      fullWidth
      :disabled="isDisabled"
      :isLoading="isLoading || onAddToAutoDelivery.isPending"
      theme="blue"
      @add-to-cart="onAddToAutoDeliveryClick"
    >
      <template #add-to-cart-text>
        <slot name="add-to-cart-text">
          <SmallBodyText>
            <template v-if="onAddToAutoDelivery.isPending">Adding</template>
            <template v-else-if="mode === 'recurring'">Subscribe</template>
            <template v-else>Ship One-Time</template>
          </SmallBodyText>
        </slot>
      </template>
    </AddToCartOrCustomize>
  </Transition>
</template>
